import { usePowerApps } from '@/context';
import {
  cleanEndSlash,
  POWER_APP_PATH_PARAM_NAME,
} from '@/features/PowerApps/hooks/usePowerAppsDeepNavigation';
import React, { useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { Language } from '../features/GlobalHeaderContainer/GlobalHeader/GlobalHeader.interface';
import { AppContext } from '../store';
import { HrefLangType } from '../store/hrefLang/hrefLang.interface';
import { isPortalPathname } from '../utils/portal';

const translateLanguageList = (
  defaultLanguageList: Language[] = [],
  hrefLanguage: HrefLangType[] = [],
) => {
  // find the language url in the Href Language object
  const findTranslation = (languageItem: Language) =>
    hrefLanguage.find((hrefLanguageItem) => languageItem.initial === hrefLanguageItem.hreflang);

  // translate every language in the list
  return defaultLanguageList.reduce(
    (languageAccumulator: Language[], currentLanguageItem: Language) => {
      const languageTranslation = findTranslation(currentLanguageItem);
      if (!languageTranslation) return [...languageAccumulator, currentLanguageItem];
      return [...languageAccumulator, { ...currentLanguageItem, url: languageTranslation.href }];
    },
    [],
  );
};
const persistQueryParams = ({
  pathname = '',
  translatedPages = [],
  queryParams = '',
}: {
  pathname: string;
  translatedPages: Language[];
  queryParams?: string;
}): Language[] => {
  if (!isPortalPathname(pathname)) return translatedPages;
  if (!queryParams) return translatedPages;
  if (!queryParams.startsWith(`?${POWER_APP_PATH_PARAM_NAME}=`)) return translatedPages;
  return translatedPages.map((page) => ({
    ...page,
    url: `${cleanEndSlash(page.url)}${queryParams}`,
  }));
};

export const useTranslatedLanguageList = (defaultLanguageList: any, portalLanguageList: any) => {
  const [languageList, setLanguageList] = React.useState<Language[]>([]);
  const { pathname, search = '' } = useLocation();
  const { persistedQueryParams } = usePowerApps();

  const {
    state: { hrefLang },
  } = useContext(AppContext);

  React.useEffect(() => {
    persistedQueryParams.setQueryParams({ pathname, search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const currentPageLanguageList = useMemo(() => {
    // If portal page then use a specific portal language list
    return isPortalPathname(pathname) ? portalLanguageList : defaultLanguageList;
  }, [pathname, defaultLanguageList, portalLanguageList]);

  const cleanLanguageList: Language[] = useMemo(() => {
    if (!currentPageLanguageList) return [];

    return Object.entries(currentPageLanguageList).map(([key, value]) => {
      const SEPARATOR = '|';
      const [label = '', url = ''] = (value as string).split(SEPARATOR);

      return {
        initial: key,
        displayText: label,
        url,
      };
    });
  }, [currentPageLanguageList]);

  // translate the page language list
  useDeepCompareEffect(() => {
    if (!hrefLang.length) return setLanguageList([...cleanLanguageList]);
    const translatedPages = translateLanguageList(cleanLanguageList, hrefLang);
    const translatedPagesWithQueryParams = persistQueryParams({
      pathname,
      translatedPages,
      queryParams: persistedQueryParams.queryParams,
    });

    return setLanguageList([...translatedPagesWithQueryParams]);
  }, [cleanLanguageList, hrefLang, persistedQueryParams.queryParams]);

  return {
    languageList,
  };
};
